/**
 * useAnchoredMenu — shared open/close + fixed-positioning plumbing for the
 * Nut Sheet's custom drop-downs (PlanSelect, TypeSelect).
 *
 * These controls show each option's full label in the open menu but only the
 * bare code on the collapsed trigger — behavior a native <select> can't provide.
 * The menu is rendered with `position: fixed` so it isn't clipped by the table's
 * horizontal-scroll container, and it closes on click-outside, Escape, or any
 * page/table scroll (a fixed menu would otherwise detach from its trigger).
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface MenuPos {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

export function useAnchoredMenu(minWidth = 220) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPos | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 8;
    const width = Math.max(rect.width, minWidth);
    // Clamp horizontally so the menu never runs off the right edge.
    let left = rect.left;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - width);
    }
    const top = rect.bottom + 2;
    // Cap height to the space below so the menu's own scrollbar stays on-screen.
    const maxHeight = Math.max(120, window.innerHeight - top - margin);
    setPos({ top, left, width, maxHeight });
  };

  useLayoutEffect(() => {
    if (open) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Ignore the menu's own internal scrolling — that capture-phase event also
    // reaches window and must not dismiss the menu.
    const onScroll = (e: Event) => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  return { open, setOpen, pos, btnRef, menuRef };
}
